import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";
import type { ThaiVisaType } from "@/content/visa-corpus/thai-visa-types";

type VisaCard = Pick<ThaiVisaType, "slug" | "titleTh" | "codeTh" | "whoTh">;

// Registry loads inside the loader (its own lazy chunk) so the 37 KB visa
// corpus never ships in the shared client entry bundle.
export const Route = createFileRoute("/visa/thai/")({
  loader: async (): Promise<{ types: VisaCard[] }> => {
    const { THAI_VISA_TYPES } = await import("@/content/visa-corpus/thai-visa-types");
    return {
      types: THAI_VISA_TYPES.map((v) => ({
        slug: v.slug,
        titleTh: v.titleTh,
        codeTh: v.codeTh,
        whoTh: v.whoTh,
      })),
    };
  },
  component: ThaiVisaHubTH,
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const title = "วีซ่าไทยทุกประเภท 2026 — เงื่อนไข เอกสาร และการขออยู่ต่อ";
    const description =
      "รวมวีซ่าไทย 12 ประเภท (ท่องเที่ยว TR/METV, Non-B, Non-ED, Non-O สมรส/เกษียณ, O-A, O-X, DTV, LTR, SMART) เปรียบเทียบคุณสมบัติผู้ยื่น อายุวีซ่า จำนวนวันที่อนุญาตให้อยู่ และการยื่น ตม.7 อ้างอิงหน่วยงานราชการ";
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/visa/thai",
        type: "website",
        alternates: [{ lang: "en", path: "/en/visa/thai" }],
        keywords: ["วีซ่าไทย 2026", "ประเภทวีซ่าไทย", "Non-B", "DTV", "LTR", "วีซ่าเกษียณ", "ตม.7", "รายงานตัว 90 วัน"],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: `${site}/` },
              { name: "วีซ่าไทยทุกประเภท", url: `${site}/visa/thai` },
            ]),
          ),
        },
        ...(loaderData
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "ItemList",
                  name: "ประเภทวีซ่าไทย",
                  itemListElement: loaderData.types.map((v, i) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    name: v.titleTh,
                    url: `${site}/visa/thai/${v.slug}`,
                  })),
                }),
              },
            ]
          : []),
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/visa/thai")) },
      ],
    };
  },
});

function ThaiVisaHubTH() {
  const { types } = Route.useLoaderData() as { types: VisaCard[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span> <span>วีซ่าไทยทุกประเภท</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">วีซ่าไทยทุกประเภท 2026</h1>
        <p className="mb-8 max-w-3xl text-muted-foreground">
          เลือกประเภทวีซ่าที่ตรงกับวัตถุประสงค์ของท่าน แต่ละหน้าอธิบายคุณสมบัติผู้ยื่น เอกสารที่ต้องเตรียม ขั้นตอนการยื่นผ่านระบบ e-Visa
          การขออยู่ต่อด้วยแบบ ตม.7 และข้อผิดพลาดที่พบบ่อย ทั้งนี้ &ldquo;อายุวีซ่า&rdquo; และ &ldquo;จำนวนวันที่ได้รับอนุญาตให้อยู่&rdquo; เป็นสองเรื่องที่ต่างกัน
          และการอนุมัติทุกกรณีเป็นดุลพินิจของพนักงานเจ้าหน้าที่
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          {types.map((v) => (
            <a key={v.slug} href={`/visa/thai/${v.slug}`} className="rounded-lg border bg-card p-5 transition-colors hover:border-primary">
              <h2 className="mb-1 text-lg font-semibold">{v.titleTh}</h2>
              <p className="mb-2 text-xs text-muted-foreground">{v.codeTh}</p>
              <p className="line-clamp-3 text-sm text-muted-foreground">{v.whoTh}</p>
            </a>
          ))}
        </div>

        <section className="mt-10 rounded-lg border bg-primary/5 p-6">
          <h2 className="mb-2 text-xl font-bold">ไม่แน่ใจว่าควรยื่นวีซ่าประเภทใด?</h2>
          <p className="mb-4 text-sm text-muted-foreground">
            สอบถามเจ้าหน้าที่ได้ทางโทรศัพท์ LINE หรืออีเมล จันทร์–เสาร์ 9:00–18:00 เพื่อประเมินกรณีของท่านและแจ้งค่าบริการ
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 font-medium text-primary-foreground">ติดต่อเรา →</a>
        </section>

        <p className="mt-8 text-sm text-muted-foreground">
          ดูคำถามที่พบบ่อยทั้งหมด: <a href="/visa/faq" className="text-primary underline">FAQ วีซ่าและตรวจคนเข้าเมือง</a>
        </p>

        <FaqSourceNote topic="visa" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
